export type CostInsightsBannerType = 'fusion' | 'sao' | 'cost-insights' | 'permissions';

export type MetricVisibility = 'none' | 'partial' | 'full';

export type CostInsightsUiState = {
  bannerType: CostInsightsBannerType | null;
  shouldShowBanner: boolean;
  metricVisibility: MetricVisibility;
};

/**
 * Derives metric visibility from cost feature and permission state.
 * - 'full': Cost feature enabled + permission granted
 * - 'partial': Cost feature enabled but no permission (show all tabs but cost)
 * - 'none': Cost feature not enabled
 */
const deriveMetricVisibility = (
  isCostInsightsFeatureEnabled: boolean,
  hasCostInsightsPermission: boolean,
): MetricVisibility => {
  if (isCostInsightsFeatureEnabled && hasCostInsightsPermission) return 'full';
  if (isCostInsightsFeatureEnabled) return 'partial';
  return 'none';
};

/**
 * Computes the UI state for the Cost Insights feature based on the platform state matrix.
 *
 * Decision matrix (most restrictive to least):
 * | State                                                      | Metrics | Banner           |
 * |------------------------------------------------------------|---------|------------------|
 * | SAO not eligible                                           | none    | none             |
 * | Cannot enable Fusion (flag off or ineligible)              | none    | none             |
 * | Can enable Fusion, cost perm off, cost off                 | none    | fusion           |
 * | Can enable Fusion, cost perm off, cost on                  | partial | fusion           |
 * | Can enable Fusion, cost perm on, cost on                   | full    | fusion           |
 * | Fusion Enabled, SAO off, cost perm off, cost off           | none    | sao              |
 * | Fusion Enabled, SAO off, cost perm off, cost on            | partial | sao              |
 * | Fusion Enabled, SAO off, cost perm on, cost on             | full    | sao              |
 * | Fusion Enabled, SAO on, cost perm off, cost off            | none    | cost-insights    |
 * | Fusion Enabled, SAO on, cost perm off, cost on             | partial | permissions      |
 * | Fusion Enabled, SAO on, cost perm on, cost on              | full    | none             |
 */
export const getCostInsightsUiState = ({
  isSaoEligible,
  isUsingFusion,
  isSaoEnabledForProject,
  isCostInsightsFeatureEnabled,
  hasCostInsightsPermission,
  canEnableFusion,
}: {
  isSaoEligible: boolean;
  isUsingFusion: boolean;
  isSaoEnabledForProject: boolean;
  isCostInsightsFeatureEnabled: boolean;
  hasCostInsightsPermission: boolean;
  canEnableFusion: boolean;
}): CostInsightsUiState => {
  // Row 1: SAO not eligible — hide everything
  if (!isSaoEligible) {
    return { bannerType: null, shouldShowBanner: false, metricVisibility: 'none' };
  }

  // Row 2: Cannot enable Fusion (flag off or ineligible)
  if (!isUsingFusion && !canEnableFusion) {
    return { bannerType: null, shouldShowBanner: false, metricVisibility: 'none' };
  }

  // Rows 3-5: Can enable Fusion but not using Fusion yet (mantle)
  if (!isUsingFusion) {
    return {
      bannerType: 'fusion',
      shouldShowBanner: true,
      metricVisibility: deriveMetricVisibility(
        isCostInsightsFeatureEnabled,
        hasCostInsightsPermission,
      ),
    };
  }

  // Rows 6-8: Using Fusion but SAO not enabled at job level
  if (!isSaoEnabledForProject) {
    return {
      bannerType: 'sao',
      shouldShowBanner: true,
      metricVisibility: deriveMetricVisibility(
        isCostInsightsFeatureEnabled,
        hasCostInsightsPermission,
      ),
    };
  }

  // Row 9: Fusion + SAO enabled, cost feature not enabled
  if (!isCostInsightsFeatureEnabled) {
    return {
      bannerType: 'cost-insights',
      shouldShowBanner: true,
      metricVisibility: 'none',
    };
  }

  // Row 10: Fusion + SAO enabled, cost feature enabled but no permission
  if (!hasCostInsightsPermission) {
    return {
      bannerType: 'permissions',
      shouldShowBanner: true,
      metricVisibility: 'partial',
    };
  }

  // Row 11: All conditions met
  return { bannerType: null, shouldShowBanner: false, metricVisibility: 'full' };
};
